import { useRef, useCallback } from "react";
import { useService, useServiceEventListener } from "@/hooks/useService";
import { useDataStreamEventBus } from "@/contexts/dataStreamEventBusContext";
import { SentenceTokenizer } from "./tokenizer";
import { resolveStreamId, resolveVoice, resolveBlockEntries, type BlockEntry } from "./resolve";
import { AGENT_SERVICE_V2_IDENT, type IAgentServiceV2 } from "@bloomstack/apis/agentTypes";
import {
  WORKSPACE_LIBRARY_SERVICE_IDENT,
  type IWorkspaceLibraryService,
} from "@bloomstack/apis/workspace";
import {
  DATASTREAM_MANAGER_SERVICE_IDENT,
  RPC_EVENT_STREAM_STATE_CHANGED_IDENT,
  type IDataStreamManagerService,
  type StreamState,
  type StreamStateChangedEvent,
} from "@bloomstack/apis/services/dataStreamManager/IDataStreamManagerService";
import type { ContentDelta, LLMHistoryContent, LLMHistory } from "@bloomstack/apis/llmTypes";
import type { TTSChunk, TTSRef } from "./types";

/**
 * Options wiring capture into playback. Capture never touches audio — it only
 * emits finalized {@link TTSChunk}s and registers/retires itself as a producer
 * so playback knows when the queue can finally drain.
 */
export interface TTSCaptureDeps {
  emitChunk: (chunk: TTSChunk) => void;
  addProducer: () => void;
  removeProducer: () => void;
  isMuted: (workspaceId: string, userProfileId: string) => boolean;
}

/**
 * CAPTURE — the first half of the pipeline: collect tokens → create chunks. It
 * reacts to the data-stream event bus + store and knows NOTHING about audio.
 * For one agent turn (a block = root entry + tool-call child entries) it feeds
 * text, in reading order, into a per-turn {@link SentenceTokenizer}; the
 * tokenizer owns ALL sentence-boundary decisions and emits chunks via
 * `deps.emitChunk`.
 *
 * Sources are unified: the fast Dexie snapshot seeds already-settled text; the
 * live `streamDelta` token stream carries the in-flight tail. Completion is the
 * `streamStateChanged` event, but because that rides a different RPC channel
 * than the tokens, we do NOT detach on it — we mark the turn "done" and retire
 * only once every known unit is finalized, so trailing tokens are never lost.
 */
export function useTTSCapture(deps: TTSCaptureDeps) {
  const agentService = useService<IAgentServiceV2>(AGENT_SERVICE_V2_IDENT);
  const workspaceService = useService<IWorkspaceLibraryService>(WORKSPACE_LIBRARY_SERVICE_IDENT);
  const dataStreamService = useService<IDataStreamManagerService>(DATASTREAM_MANAGER_SERVICE_IDENT);
  const eventBus = useDataStreamEventBus(false);

  const depsRef = useRef(deps);
  depsRef.current = deps;

  // A per-unit accumulator — monotonic, de-duplicated text per content item.
  type UnitBuf = {
    entryId: string;
    key: string;
    text: string;
    pushed: number;    // chars already handed to the tokenizer
    final: boolean;    // finalized (isStreaming === false) — remainder flushed
  };

  // One agent turn. Owns the bus-subscription lifetime + tokenizer; retires as
  // a producer when done.
  type Capture = {
    streamId: string;
    rootEntryId: string;
    workspaceId: string;
    userProfileId: string;
    voice: string;
    streaming: boolean;
    blockIds: Set<string>;
    units: Map<string, UnitBuf>;
    order: string[];
    tokenizer: SentenceTokenizer;
    streamDone: boolean;   // stream reported not-running (no NEW units expected)
    complete: boolean;     // retired
    detach: () => void;
  };
  const unitKey = (entryId: string, contentIndex: number) => `${entryId}:${contentIndex}`;

  const liveCapturesRef = useRef<Set<Capture>>(new Set());

  // Live stream state per streamId, driven by the streamStateChanged RPC event.
  const streamStatesRef = useRef<Map<string, StreamState>>(new Map());
  const streamStateSubsRef = useRef<Set<(streamId: string, state: StreamState) => void>>(new Set());
  useServiceEventListener<StreamStateChangedEvent>(
    RPC_EVENT_STREAM_STATE_CHANGED_IDENT,
    (eventStreamId, state) => {
      streamStatesRef.current.set(eventStreamId, state);
      for (const cb of streamStateSubsRef.current) cb(eventStreamId, state);
    },
    [],
  );

  // Accumulate text for a content item; hand only the NEW suffix to the
  // tokenizer, in reading order. Returns true if it's speakable text.
  // `finalize` = the content is DONE (no more text). The backend signals this
  // by EMITTING contentUpdated at all (it only fires on finalize) — NOT by
  // setting isStreaming=false, which brain5 omits from the finalize payload. So
  // the caller passes finalize=true from the contentUpdated handler.
  const feedContent = useCallback((
    cap: Capture,
    entryId: string,
    content: LLMHistoryContent,
    finalize = false,
  ): boolean => {
    // A NEW content item of ANY kind other than the text we're currently
    // buffering means that text is DONE — whatever this is (thinking, tool_use,
    // internal, a later text block) is ordered after it. Flush the current
    // buffer NOW. This must run BEFORE any early-return guard, or a thinking
    // block that lacks orderIndex would slip past and the text would stall.
    const isBufferableText =
      content.type === "text" &&
      !content.internal &&
      content.orderIndex !== undefined;
    if (!isBufferableText) {
      cap.tokenizer.flushCurrent();
      return false;
    }
    const key = unitKey(entryId, content.orderIndex!);
    let u = cap.units.get(key);
    if (!u) {
      u = { entryId, key, text: "", pushed: 0, final: false };
      cap.units.set(key, u);
      cap.order.push(key);
    }
    const incoming = content.text ?? "";
    if (incoming.length > u.text.length) u.text = incoming;
    if (u.text.length > u.pushed) {
      cap.tokenizer.push(entryId, key, u.text.slice(u.pushed));
      u.pushed = u.text.length;
    }
    // Finalized text gets no trailing boundary (the next thing is often a
    // tool_use), so flush its held remainder now — else it stalls until the
    // next unit begins. Trigger on the finalize signal (contentUpdated fired)
    // OR the legacy isStreaming===false, whichever we see. Then re-check
    // completion.
    if ((finalize || content.isStreaming === false) && !u.final) {
      u.final = true;
      cap.tokenizer.flushUnit(key);
      tryCompleteRef.current(cap);
    }
    return true;
  }, []);

  const feedDelta = useCallback((cap: Capture, entryId: string, contentIndex: number, suffix: string): void => {
    const key = unitKey(entryId, contentIndex);
    const u = cap.units.get(key);
    if (!u) return; // structure/classification comes from contentAdded/seed
    u.text += suffix;
    cap.tokenizer.push(entryId, key, u.text.slice(u.pushed));
    u.pushed = u.text.length;
  }, []);

  // Feed a whole entry's content in READING ORDER (by orderIndex). Order is
  // critical: the "non-text ⇒ preceding text is done" flush rule only holds if
  // we present content chronologically, so we can't rely on array order.
  const feedEntry = useCallback((cap: Capture, entryId: string, entry: LLMHistory): void => {
    cap.blockIds.add(entryId);
    const contents = [...(entry.content ?? [])].sort(
      (a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0),
    );
    for (const c of contents) feedContent(cap, entryId, c as LLMHistoryContent);
  }, [feedContent]);

  const seedFromStore = useCallback(async (cap: Capture, preresolved?: BlockEntry[]): Promise<void> => {
    const block = preresolved ?? (await resolveBlockEntries(cap.streamId, cap.rootEntryId, dataStreamService));
    for (const be of block) feedEntry(cap, be.entryId, be.entry);
  }, [dataStreamService, feedEntry]);

  const completeCapture = useCallback((cap: Capture) => {
    if (cap.complete) return;
    cap.complete = true;
    cap.tokenizer.end();
    // No more text will ever come for this turn: emit ONE done sentinel behind
    // every real chunk. Playback finishes only once it drains with nothing left.
    depsRef.current.emitChunk({
      text: "",
      voice: cap.voice,
      entryId: cap.rootEntryId,
      unitKey: `${cap.rootEntryId}:done`,
      rootEntryId: cap.rootEntryId,
      baseOffset: 0,
      done: true,
    });
    cap.detach();
    liveCapturesRef.current.delete(cap);
    depsRef.current.removeProducer();
  }, []);

  // Stream not-running = no more text will ever come. Every delta has already
  // been pushed to the tokenizer, and completeCapture's tokenizer.end() flushes
  // the remainder — so streamDone alone is sufficient to retire. (The old
  // per-unit `final` gate never opened: brain5 omits the finalize signal, so a
  // unit stayed non-final forever and the capture never completed.)
  const tryCompleteRef = useRef<(cap: Capture) => void>(() => {});
  tryCompleteRef.current = (cap: Capture) => {
    if (!cap.streamDone || cap.complete) return;
    completeCapture(cap);
  };

  const makeCapture = useCallback((
    streamId: string,
    rootEntryId: string,
    workspaceId: string,
    userProfileId: string,
    voice: string,
    streaming: boolean,
    preresolved?: BlockEntry[],
  ): Capture => {
    const muted = depsRef.current.isMuted(workspaceId, userProfileId);
    const cap: Capture = {
      streamId,
      rootEntryId,
      workspaceId,
      userProfileId,
      voice,
      streaming,
      blockIds: new Set<string>([rootEntryId]),
      units: new Map(),
      order: [],
      // Muted turn: swallow chunks (still consume text so ordering/lifetime are
      // correct, but nothing reaches synthesis).
      tokenizer: new SentenceTokenizer(voice, rootEntryId, muted ? () => {} : depsRef.current.emitChunk),
      streamDone: false,
      complete: false,
      detach: () => {},
    };

    depsRef.current.addProducer();
    liveCapturesRef.current.add(cap);

    if (preresolved) {
      for (const be of preresolved) feedEntry(cap, be.entryId, be.entry);
    }

    if (!streaming || !eventBus) {
      if (preresolved) {
        completeCapture(cap);
      } else {
        void seedFromStore(cap).then(() => completeCapture(cap));
      }
      return cap;
    }

    // LIVE turn: subscribe first (so we miss nothing), then seed.
    // contentAdded/contentUpdated share logic, but contentUpdated ONLY fires on
    // finalize (brain5 uses deltas while streaming, updateContent at "end"), so
    // it carries finalize=true — that's the reliable "this text is done" signal
    // (isStreaming is omitted from the finalize payload).
    const handleContent = (content: LLMHistoryContent, finalize: boolean) => {
      // ANY content arriving after the current text is a trigger: the text is
      // done. If it belongs to this block, feedContent buffers text / flushes on
      // non-text. If it's outside the block entirely, it still proves our text
      // is finished — flush the current buffer.
      if (content.entryId && cap.blockIds.has(content.entryId)) {
        feedContent(cap, content.entryId, content, finalize);
      } else {
        cap.tokenizer.flushCurrent();
      }
    };
    const onContentAdded = (_sid: string, content: LLMHistoryContent) => handleContent(content, false);
    const onContentUpdated = (_sid: string, content: LLMHistoryContent) => handleContent(content, true);
    const onEntryAdded = (_sid: string, entry: LLMHistory) => {
      // ANY new entry arriving after the current text is a trigger: the text is
      // done. Flush now, no matter whose entry it is.
      cap.tokenizer.flushCurrent();
      // A turn is a tree: each tool call spawns a child entry parented in — pull
      // its text into this block so it's spoken too.
      if (entry.id && entry.parentId && cap.blockIds.has(entry.parentId) && !cap.blockIds.has(entry.id)) {
        feedEntry(cap, entry.id, entry);
      }
    };
    const onDelta = (delta: ContentDelta) => {
      if (delta.type !== "string" || !cap.blockIds.has(delta.entryId)) return;
      feedDelta(cap, delta.entryId, delta.contentIndex, delta.delta);
    };

    eventBus.events.subscribe(streamId, "contentAdded", onContentAdded);
    eventBus.events.subscribe(streamId, "contentUpdated", onContentUpdated);
    eventBus.events.subscribe(streamId, "entryAdded", onEntryAdded);
    eventBus.events.subscribe(streamId, "streamDelta", onDelta);

    // A DEFINED entryId outside this block = a different turn started → truly
    // finished. Otherwise not-running only means "no NEW units expected";
    // trailing deltas may still be in flight, so mark streamDone + tryComplete.
    const onState = (sid: string, state: StreamState) => {
      if (sid !== streamId) return;
      const movedAway = !!state.entryId && !cap.blockIds.has(state.entryId);
      if (movedAway) {
        completeCapture(cap);
      } else if (!state.isRunning) {
        cap.streamDone = true;
        tryCompleteRef.current(cap);
      }
    };
    streamStateSubsRef.current.add(onState);

    cap.detach = () => {
      eventBus.events.unsubscribe(streamId, "contentAdded", onContentAdded);
      eventBus.events.unsubscribe(streamId, "contentUpdated", onContentUpdated);
      eventBus.events.unsubscribe(streamId, "entryAdded", onEntryAdded);
      eventBus.events.unsubscribe(streamId, "streamDelta", onDelta);
      streamStateSubsRef.current.delete(onState);
    };

    void seedFromStore(cap).then(() => {
      const known = streamStatesRef.current.get(streamId);
      if (known && !known.isRunning) {
        cap.streamDone = true;
        tryCompleteRef.current(cap);
      }
    });

    return cap;
  }, [eventBus, feedEntry, feedDelta, seedFromStore, completeCapture]);

  /**
   * Resolve each ref into a capture. A ref is one agent turn; "play from here"
   * passes many. Blocks are deduped so a ref inside another ref's turn tree
   * isn't spoken twice. Captures begin producing immediately.
   */
  const captureRefs = useCallback(async (refs: TTSRef[], streaming: boolean, signal: AbortSignal) => {
    const voiceCache = new Map<string, string | null>();
    const covered = new Set<string>();
    for (const ref of refs) {
      if (signal.aborted) return;
      if (depsRef.current.isMuted(ref.workspaceId, ref.userProfileId)) continue;
      if (covered.has(ref.entryId)) continue;
      const voice = await resolveVoice(ref.userProfileId, voiceCache, agentService);
      if (!voice) continue;
      const streamId = await resolveStreamId(ref.workspaceId, workspaceService);
      if (!streamId) continue;
      if (signal.aborted) return;

      if (streaming) {
        // LIVE turn (auto-speak): single ref. Subscribe IMMEDIATELY — no block
        // resolve first, or we'd miss early deltas while fetching.
        makeCapture(streamId, ref.entryId, ref.workspaceId, ref.userProfileId, voice, true);
        covered.add(ref.entryId);
        continue;
      }

      // COMPLETED data: resolve the block up front so dedup is synchronous —
      // play-from-here passes tool-call child entries belonging to a preceding
      // turn's block; without this they'd each spawn a capture and repeat text.
      const block = await resolveBlockEntries(streamId, ref.entryId, dataStreamService);
      if (signal.aborted) return;
      const cap = makeCapture(streamId, ref.entryId, ref.workspaceId, ref.userProfileId, voice, false, block);
      covered.add(ref.entryId);
      for (const be of block) covered.add(be.entryId);
      for (const id of cap.blockIds) covered.add(id);
    }
  }, [agentService, workspaceService, dataStreamService, makeCapture]);

  /** One-shot producer for raw preview text (no entry). */
  const captureText = useCallback((text: string, voice: string) => {
    depsRef.current.addProducer();
    const tok = new SentenceTokenizer(voice, "preview", depsRef.current.emitChunk);
    tok.push("preview", "preview:0", text);
    tok.end();
    depsRef.current.removeProducer();
  }, []);

  /** Detach all live captures (used by stop()). */
  const detachAll = useCallback(() => {
    for (const cap of liveCapturesRef.current) cap.detach();
    liveCapturesRef.current.clear();
  }, []);

  return { captureRefs, captureText, detachAll };
}
